const carouselFactory = ($, _) => {
  const touchFactory = (onLeft, onRight) => {
    const min_horizontal_move = 30;
    const max_vertical_move = 30;
    const within_ms = 1000;

    let start_xPos;
    let start_yPos;
    let start_time;

    const touchStart = (e) => {
      start_xPos = e.touches[0].pageX;
      start_yPos = e.touches[0].pageY;
      start_time = new Date();
    };

    const touchEnd = (e) => {
      const end_xPos = e.changedTouches[0].pageX;
      const end_yPos = e.changedTouches[0].pageY;
      const move_x = end_xPos - start_xPos;
      const move_y = end_yPos - start_yPos;
      const elapsed_time = new Date() - start_time;
      if (
        Math.abs(move_x) > min_horizontal_move &&
        Math.abs(move_y) < max_vertical_move &&
        elapsed_time < within_ms
      ) {
        if (move_x < 0) {
          onLeft();
        } else {
          onRight();
        }
      }
    };

    return [touchStart, touchEnd];
  };

  /**
   * ------------------------------------------------------------------------
   * Constants
   * ------------------------------------------------------------------------
   */
  const ACTIVE = 'active';
  const NEXT = 'carousel-item-next';
  const PREV = 'carousel-item-prev';
  const RIGHT = 'carousel-item-right';
  const LEFT = 'carousel-item-left';
  const ANIMATING = 'animating';
  const DATA_SLIDE_TO = 'data-slide-to';
  const TOUCHEVENT_COMPAT_WAIT = 500;

  /* return */
  return (options, container, items, left, right, indicators) => {
    const _defaults = {
      interval: 5000,
      keyboard: true,
      pause: 'hover',
      wrap: true,
      duration: 600,
      easing: 'easeOutQuart',
      onChange: () => {},
      addSwipeEvent: false,
    };

    const _opts = _.merge(options, _defaults);
    let _intervalId;
    let _isPaused;
    const _indicators = [];
    const autoplay = () => _opts.interval;
    const isInfinit = () => _opts.wrap;

    const activeIndex = () => items.findIndex((x) => $.hasClass(ACTIVE, x));

    const isSliding = () => $.hasClass(ANIMATING, container);

    const setActiveIndicator = (from, to) => {
      if (from === to) {
        return;
      }
      if (_indicators[from]) {
        $.removeClass(ACTIVE, _indicators[from]);
      }

      if (_indicators[to]) {
        $.addClass(ACTIVE, _indicators[to]);
      }
    };

    const slide = (direction, from, to) => {
      if (autoplay()) {
        pause();
      }

      if (indicators) {
        setActiveIndicator(from, to);
      }

      _opts.onChange(from, to); // there also could be onChangeEnd

      $.addClass(ANIMATING, container);

      const orderClassName = direction === LEFT ? NEXT : PREV;
      $.addClass(orderClassName, items[to]);
      $.reflow(items[to]);
      $.addClass(direction, items[from]);
      $.addClass(direction, items[to]);

      setTimeout(() => {
        $.removeClasses([orderClassName, direction], items[to]);
        $.addClass(ACTIVE, items[to]);
        $.removeClasses([ACTIVE, direction], items[from]);

        $.removeClass(ANIMATING, container);
        if (autoplay()) {
          play();
        }
      }, _opts.duration);
    };

    const set = (to) => {
      if (to < 0 || to > items.length - 1 || isSliding()) {
        return;
      }

      const from = activeIndex();

      if (to === from) {
        if (autoplay()) {
          pause();
          play();
        }
        return;
      }

      slide(from < to ? LEFT : RIGHT, from, to);
    };

    const _next = () => {
      if (isSliding()) {
        return;
      }
      const from = activeIndex();
      if (!isInfinit() && from === items.length - 1) {
        return;
      }
      slide(LEFT, from, from === items.length - 1 ? 0 : from + 1);
    };

    const _prev = () => {
      if (isSliding()) {
        return;
      }
      const from = activeIndex();
      if (!isInfinit() && from === 0) {
        return;
      }
      slide(RIGHT, from, from === 0 ? items.length - 1 : from - 1);
    };

    const nextWhenVisible = () => {
      // Don't call next when the page isn't visible
      // or the carousel or its parent isn't visible
      if (
        !$.isDocHidden() &&
        $.isVisible(container) &&
        $.getCssValue('visibility', container) !== 'hidden'
      ) {
        _next();
      }
    };

    const pause = () => {
      _isPaused = true;
      clearInterval(_intervalId);
      _intervalId = null;
    };

    const play = () => {
      _isPaused = false;

      if (_intervalId) {
        clearInterval(_intervalId);
        _intervalId = null;
      }

      if (!_isPaused) {
        _intervalId = setInterval(
          document.visibilityState ? nextWhenVisible : _next,
          _opts.interval,
        );
      }
    };

    const onIndicatorFactory = (to) => {
      return (e) => {
        e.preventDefault();
        setActiveIndicator(activeIndex(), to);
        set(to);
      };
    };

    const handleIndicatorClickFactory = _.memoizeWith(
        (i) => i.toString(),
        onIndicatorFactory,
    );

    const bindHover = (fn) => {
      fn('mouseenter', pause, container);
      fn('mouseleave', play, container);
      if ('ontouchstart' in document.documentElement) {
        // If it's a touch-enabled device, mouseenter/leave are fired as
        // part of the mouse compatibility events on first tap - the carousel
        // would stop cycling until user tapped out of it;
        // here, we listen for touchend, explicitly pause the carousel
        // (as if it's the second time we tap on it, mouseenter compat event
        // is NOT fired) and after a timeout (to allow for mouse compatibility
        // events to fire) we explicitly restart cycling
        // $(this._element).on(Event.TOUCHEND, () => {
        //   this.pause()
        //   if (this.touchTimeout) {
        //     clearTimeout(this.touchTimeout)
        //   }
        //   this.touchTimeout = setTimeout((event) => this.cycle(event), TOUCHEVENT_COMPAT_WAIT + this._config.interval)
        // })
      }
    };

    const justPreventDefault = (e) => {
      e.preventDefault();
    };

    const touchFactoryOnce = _.memoizeWith(_.always('1'), touchFactory);

    const next = (e) => {
      e.preventDefault();
      _next();
    };

    const prev = (e) => {
      e.preventDefault();
      _prev();
    };

    const bindEvents = (fn) => {
      if (items.length < 2) {
        fn('click', justPreventDefault, left);
        fn('click', justPreventDefault, right);
        return;
      }

      fn('click', prev, left);
      fn('click', next, right);

      _indicators.forEach((x, i) => {
        if (x) {
          // console.log(fn.toString().substring(28, 39), i, handleIndicatorClickFactory(i) === handleIndicatorClickFactory(i))
          fn('click', handleIndicatorClickFactory(i), x);
        }
      });

      if (_opts.pause === 'hover' && autoplay()) {
        bindHover(fn);
      }

      if (_opts.addSwipeEvent) {
        fn('touchstart', touchFactoryOnce(_next, _prev)[0], container);
        fn('touchend', touchFactoryOnce(_next, _prev)[1], container);
      }
    };

    const destroy = () => {
      bindEvents($.off);
    };

    const init = () => {
      bindEvents($.on);
      if (autoplay()) {
        play();
      }
    };

    /* indicators */
    if (indicators) {
      indicators.forEach((x) => {
        _indicators[$.getAttribute(DATA_SLIDE_TO, x)] = x;
      });
    }

    init();

    return {
      prev: _prev,
      next: _next,
      set: set,
      destroy: destroy,
    };
  };
};

const carouselMock = () => ({
  prev: () => {},
  next: () => {},
  set: () => {},
  destroy: () => {},
});

export default carouselFactory;

export {carouselMock};
